import { useState } from "react";
import { Search } from "lucide-react";

import {
  DisqualifiedTable,
  PatientDetailsSideModal,
  PatientStatusPill,
  PatientTable,
  TransferTable,
} from "@/components";
import { useDebounce } from "@/hooks";
import { PatientCasesLayout } from "@/layout/PatientCasesLayout";
import { Input, Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui";
import { PATIENTS_LIST_STATUS } from "@/utils";
import type { PatientsListStatus } from "@/utils";

export function PatientsList() {
  const [fullNameSearchQuery, setFullNameSearchQuery] = useState("");
  const [isSideModalOpen, setIsSideModalOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const debouncedQuery = useDebounce(fullNameSearchQuery);
  const [activeTab, setActiveTab] = useState<PatientsListStatus>(
    PATIENTS_LIST_STATUS.INTAKE,
  );

  const handleRowClick = (patientId: string) => {
    setSelectedPatientId(patientId);
    setIsSideModalOpen(true);
  };

  return (
    <PatientCasesLayout>
      <Tabs
        defaultValue={PATIENTS_LIST_STATUS.INTAKE}
        className="flex flex-col gap-2"
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value as PatientsListStatus);
          setFullNameSearchQuery("");
          setSelectedPatientId("");
        }}
      >
        <div className="flex items-center justify-between pb-2">
          <h1 className="text-3xl font-medium text-primary-950">Cases</h1>
          <PatientStatusPill status={activeTab} />
        </div>
        <div className="flex items-center justify-between">
          <Input
            placeholder="Search"
            icon={<Search size={16} />}
            value={fullNameSearchQuery}
            onChange={(e) => setFullNameSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <TabsList>
            <TabsTrigger value={PATIENTS_LIST_STATUS.INTAKE}>
              Patient
            </TabsTrigger>
            <TabsTrigger value={PATIENTS_LIST_STATUS.TRANSFER}>
              Transfer
            </TabsTrigger>
            <TabsTrigger value={PATIENTS_LIST_STATUS.DISQUALIFIED}>
              Disqualified
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={PATIENTS_LIST_STATUS.INTAKE}>
          <PatientTable
            searchQuery={debouncedQuery}
            handleRowClick={handleRowClick}
          />
        </TabsContent>

        <TabsContent value={PATIENTS_LIST_STATUS.TRANSFER}>
          <TransferTable
            searchQuery={debouncedQuery}
            handleRowClick={handleRowClick}
          />
        </TabsContent>

        <TabsContent value={PATIENTS_LIST_STATUS.DISQUALIFIED}>
          <DisqualifiedTable searchQuery={debouncedQuery} />
        </TabsContent>
      </Tabs>
      <PatientDetailsSideModal
        isOpen={isSideModalOpen}
        onOpenChange={(open) => {
          setIsSideModalOpen(open);
          if (!open) setSelectedPatientId("");
        }}
        patientId={selectedPatientId}
        activeTab={activeTab}
      />
    </PatientCasesLayout>
  );
}
